import { useMemo } from 'react';

/**
 * Custom hook to process and prepare map data from API responses
 * @param {Object} rawResponse - The raw API response containing map data
 * @param {string} chartType - The chart type (should be 'map' for this hook)
 * @returns {Object|null} Processed map data or null if invalid
 */
export const useMapData = (rawResponse, chartType) => {
  return useMemo(() => {
    const interpretation = rawResponse?.interpretation || rawResponse;

    // Only process if chart type is map
    if (chartType !== 'map' || !interpretation?.map) {
      return null;
    }

    // Extract map configuration
    const { nameColumn, nameLabel } = interpretation.map;

    // Handle potential typo in API response (longitureColumn vs longitudeColumn)
    const latitudeColumn = interpretation.map.latitudeColumn;
    const longitudeColumn = interpretation.map.longitudeColumn ||
      interpretation.map.longitureColumn;

    // Validate required fields
    if (!latitudeColumn || !longitudeColumn || !rawResponse?.results?.rows?.length) {
      return null;
    }

    try {
      // Process each row to create markers
      const validMarkers = rawResponse.results.rows
        .map(item => {
          const lat = parseFloat(item[latitudeColumn]);
          const lon = parseFloat(item[longitudeColumn]);

          if (isNaN(lat) || isNaN(lon) ||
            lat < -90 || lat > 90 ||
            lon < -180 || lon > 180) {
            return null;
          }

          return {
            lat,
            lon,
            name: item[nameColumn] || 'Location',
            color: 'black'
          };
        })
        .filter(marker => marker !== null);

      if (validMarkers.length === 0) {
        return {
          center: [0, 0],
          zoom: 2,
          markers: []
        };
      }

      // Calculate bounds and center
      const lats = validMarkers.map(marker => marker.lat);
      const lons = validMarkers.map(marker => marker.lon);

      const minLat = Math.min(...lats);
      const maxLat = Math.max(...lats);
      const minLon = Math.min(...lons);
      const maxLon = Math.max(...lons);

      let centerLat = (minLat + maxLat) / 2;
      let centerLon = (minLon + maxLon) / 2;

      // Calculate zoom level
      let zoom;
      if (validMarkers.length === 1) {
        zoom = 13; // Single marker - zoom in close
      } else {
        const latDiff = maxLat - minLat;
        let lonDiff = maxLon - minLon;

        // Handle international date line crossing
        if (lonDiff > 180) {
          const adjustedLons = lons.map(lon => lon < 0 ? lon + 360 : lon);
          const adjustedMinLon = Math.min(...adjustedLons);
          const adjustedMaxLon = Math.max(...adjustedLons);

          const diff1 = adjustedMaxLon - adjustedMinLon;
          const diff2 = (adjustedMinLon + 360) - adjustedMaxLon;

          lonDiff = Math.min(diff1, diff2);

          // Recalculate center for date line crossing
          if (diff1 <= diff2) {
            const adjustedCenterLon = (adjustedMinLon + adjustedMaxLon) / 2;
            centerLon = adjustedCenterLon > 180 ? adjustedCenterLon - 360 : adjustedCenterLon;
          } else {
            let tempCenterLon = (adjustedMaxLon + (adjustedMinLon + 360)) / 2;
            tempCenterLon = tempCenterLon % 360;
            if (tempCenterLon > 180) tempCenterLon -= 360;
            centerLon = tempCenterLon;
          }
        }

        // Calculate zoom based on maximum span
        const maxSpan = Math.max(latDiff, lonDiff);

        if (maxSpan > 180) zoom = 1;
        else if (maxSpan > 120) zoom = 2;
        else if (maxSpan > 60) zoom = 3;
        else if (maxSpan > 30) zoom = 4;
        else if (maxSpan > 15) zoom = 5;
        else if (maxSpan > 8) zoom = 6;
        else if (maxSpan > 4) zoom = 7;
        else if (maxSpan > 2) zoom = 8;
        else if (maxSpan > 1) zoom = 9;
        else if (maxSpan > 0.5) zoom = 10;
        else if (maxSpan > 0.2) zoom = 11;
        else if (maxSpan > 0.1) zoom = 12;
        else zoom = 13;
      }

      return {
        center: [centerLat, centerLon],
        zoom,
        markers: validMarkers
      };
    } catch (error) {
      console.error('Error processing map data:', error);
      return null;
    }
  }, [rawResponse, chartType]);
};

export default useMapData;
